/**
 * Branch condition evaluation logic.
 * Determines which branches are "resolved" based on event statuses.
 */
/**
 * Evaluate whether a branch condition is satisfied.
 * Returns true if the branch is taken (active/resolved).
 */
export function evaluateBranchCondition(branch, events) {
    const sourceEvent = events.find((e) => e.id === branch.sourceEventId);
    if (!sourceEvent)
        return false;
    if (sourceEvent.status === 'unchecked')
        return false;
    const { condition } = branch;
    if (condition.type === 'status') {
        return evalStatusExpression(condition.expression, sourceEvent);
    }
    if (condition.type === 'date') {
        return evalDateExpression(condition.expression, sourceEvent);
    }
    if (condition.type === 'composite') {
        return evalCompositeExpression(condition.expression, events);
    }
    return false;
}
function evalStatusExpression(expr, event) {
    const trimmed = expr.trim();
    // Pattern: "event.status === 'value'"
    const match = trimmed.match(/event\.status\s*===?\s*['"](\w+)['"]/);
    if (match) {
        return event.status === match[1];
    }
    // Pattern: "event.status !== 'value'"
    const neqMatch = trimmed.match(/event\.status\s*!==?\s*['"](\w+)['"]/);
    if (neqMatch) {
        return event.status !== neqMatch[1];
    }
    // Fallback: treat any non-unchecked status as resolved
    return event.status !== 'unchecked';
}
function evalDateExpression(expr, event) {
    const trimmed = expr.trim();
    // Pattern: "event.date > '2024-06-01'"
    const match = trimmed.match(/event\.date\s*([><=!]+)\s*['"]([^'"]+)['"]/);
    if (match && event.dateValue) {
        const [, op, dateStr] = match;
        const evDate = new Date(event.dateValue).getTime();
        const cmpDate = new Date(dateStr).getTime();
        switch (op) {
            case '>': return evDate > cmpDate;
            case '>=': return evDate >= cmpDate;
            case '<': return evDate < cmpDate;
            case '<=': return evDate <= cmpDate;
            case '===':
            case '==': return evDate === cmpDate;
            case '!==':
            case '!=': return evDate !== cmpDate;
        }
    }
    return false;
}
function evalCompositeExpression(expr, events) {
    // Simple AND/OR evaluation
    // Pattern: (eventA.status === 'success') AND (eventB.status === 'success')
    const andParts = expr.split(/\bAND\b/i);
    if (andParts.length > 1) {
        return andParts.every((part) => evalSingleExpr(part.trim(), events));
    }
    const orParts = expr.split(/\bOR\b/i);
    if (orParts.length > 1) {
        return orParts.some((part) => evalSingleExpr(part.trim(), events));
    }
    return evalSingleExpr(expr, events);
}
function evalSingleExpr(expr, events) {
    // Strip parens
    const clean = expr.replace(/[()]/g, '').trim();
    // Match eventId.status === 'value'
    const match = clean.match(/(\w[\w-]*)\s*\.\s*status\s*===?\s*['"](\w+)['"]/);
    if (match) {
        const ev = events.find((e) => e.id === match[1]);
        return ev ? ev.status === match[2] : false;
    }
    return false;
}
/**
 * Determine which timelines and events are on the "active path".
 * If a branch is active, the rest of the source timeline (events after the branch point)
 * should be considered "discarded" or inactive.
 */
export function computeActiveStates(timelines, branches, events) {
    const activeTimelines = new Set();
    const inactiveEvents = new Set();
    // Start with root timelines
    for (const tl of timelines) {
        if (!tl.parentId) {
            activeTimelines.add(tl.id);
        }
    }
    // Group events by timeline and sort chronologically
    const timelineEvents = new Map();
    for (const ev of events) {
        if (!timelineEvents.has(ev.timelineId))
            timelineEvents.set(ev.timelineId, []);
        timelineEvents.get(ev.timelineId).push(ev);
    }
    for (const [_, evs] of timelineEvents.entries()) {
        evs.sort((a, b) => a.dateValue.localeCompare(b.dateValue));
    }
    // We'll use a queue-based approach to propagate activation
    // and handle the "discard rest of timeline" logic.
    const queue = Array.from(activeTimelines);
    const processed = new Set();
    while (queue.length > 0) {
        const tlId = queue.shift();
        if (processed.has(tlId))
            continue;
        processed.add(tlId);
        const evs = timelineEvents.get(tlId) ?? [];
        let timelineDiscarded = false;
        for (const ev of evs) {
            if (timelineDiscarded) {
                inactiveEvents.add(ev.id);
            }
            // Check if any active branch starts from this event
            const outgoingBranches = branches.filter(b => b.sourceEventId === ev.id);
            for (const branch of outgoingBranches) {
                const isBranchActive = evaluateBranchCondition(branch, events);
                if (isBranchActive) {
                    activeTimelines.add(branch.targetTimelineId);
                    queue.push(branch.targetTimelineId);
                    // If a branch is active, the rest of THIS timeline is discarded
                    timelineDiscarded = true;
                }
            }
        }
    }
    return { activeTimelines, inactiveEvents };
}
/**
 * Check if a timeline is "unresolved" — it has a branch leading to it
 * but none of those branches are resolved yet.
 * A timeline is considered "resolved" if at least one path leading to it is active.
 */
export function isTimelineUnresolved(timelineId, branches, events) {
    const incoming = branches.filter((b) => b.targetTimelineId === timelineId);
    if (incoming.length === 0)
        return false; // No branch → not unresolved (it's a root)
    // If ANY incoming branch is resolved, the timeline is resolved.
    // The timeline is only unresolved if NONE of its incoming branches are resolved.
    return !incoming.some((b) => evaluateBranchCondition(b, events));
}
/**
 * Get overall project completion percentage.
 */
export function computeCompletion(events) {
    if (events.length === 0)
        return 0;
    const done = events.filter((e) => e.status === 'success' || e.status === 'failure' || e.status === 'skipped').length;
    return Math.round((done / events.length) * 100);
}
